/*
 * @Author: Teemo Ding
 * @Date: 2022-02-14 16:01:35
 * @LastEditTime: 2022-06-24 16:40:00
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Communication/Detail/index.tsx
 */
import CommonHeader from '@/src/components/CommonHeader';
import RouteBlock from '@/src/constrains/RouterBlock/RouterBlock';
import { initializeCommunicationGateway } from '@/src/request/settings/communication';
import { NEW } from '@/src/tools/constant';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  CommunicationGatewayProvider,
  SendFromType,
} from '@/src/types/common/communication';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, Spin } from 'antd';
import { get } from 'lodash';
import React, { Fragment, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { DestinationType } from 'src/types/common/communication';
import Webhooks from '../../Components/Webhooks';
import CommunicationChannel from './CommunicationChannel';
import CommunicationGateway from './CommunicationGateway';
import Template from './Template';
import { breadcrumbData } from './config';
import styles from './css.module.less';

export interface IParams {
  gateway: string;
  channelType: string;
  communicationUuid: string;
}

const permissions = () => {
  const add_edit_communication_gateway = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsCommunication,
        operation: EnumOperation.add_edit_communication_gateway,
      }),
    ],
  });

  const view_communication_gateway_details_page = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsCommunication,
        operation: EnumOperation.view_communication_gateway_details_page,
      }),
    ],
  });
  const add_edit_template = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsCommunication,
        operation: EnumOperation.add_edit_template,
      }),
    ],
  });
  const delete_template = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsCommunication,
        operation: EnumOperation.delete_template,
      }),
    ],
  });
  return {
    add_edit_communication_gateway,
    view_communication_gateway_details_page,
    add_edit_template,
    delete_template,
  };
};

const CommunicationDetail = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const {
    add_edit_communication_gateway,
    view_communication_gateway_details_page,
    add_edit_template,
    delete_template,
  } = permissions();
  const history = useHistory();
  const [form] = Form.useForm();

  const { communicationUuid, gateway } = useParams() as IParams;
  const [groupedTemplates, setGroupedTemplates] = useState([]);
  const [gatewayName, setGatewayName] = useState([]);

  const {
    data: communicationInitial = {},
    run: _initializeCommunicationGateway,
  } = useRequest(
    () =>
      initializeCommunicationGateway(
        communicationUuid === NEW ? {} : { gatewayUuid: communicationUuid },
      ),
    {
      ready: !!view_communication_gateway_details_page,
      onSuccess: (response) => {
        const { gateway: g, groupedTemplates } = response || {};
        const { name = '', remarks = '', settings } = g || {};
        const {
          accountSid = '',
          authToken = '',
          appId = '',
          host = '',
          port = '',
          destination = '',
          apiKeys = '',
          url = '',
          sendFrom = SendFromType.Mobile,
          serviceSid = '',
          areaCode = '',
          mobile: mobileNumber = '',
          areaCodeInfo,
          unsubscribeGroupId,
        } = settings || {};
        setGroupedTemplates(groupedTemplates);
        setGatewayName(name);
        const isCloopen = CommunicationGatewayProvider.Cloopen === gateway;

        form.setFieldsValue({
          name,
          remarks,
          accountSid,
          authToken,
          sendFrom,
          serviceSid,
          appId,
          host: isCloopen ? host || 'app.cloopen.com' : host,
          port: isCloopen ? port || 8883 : port,
          destination,
          apiKeys,
          url,
          unsubscribeGroupId,
          phoneNumber: { areaCode, mobileNumber, areaCodeInfo },
        });
      },
    },
  );

  const onGoBackClick = () => {
    history.push(`/settings/communicationsList`);
  };

  return (
    <Spin spinning={false}>
      <RouteBlock
        onConfirm={(f) => f()}
        okText={getI18n('general.discard')}
        scrollToFirstError
        title={getI18n('general.commonHeader.discard_header')}
        content={getI18n('general.commonHeader.discard_content')}
      />
      <CommonHeader
        breadcrumbData={breadcrumbData(gatewayName, { getI18n })}
        onGoBackClick={onGoBackClick}
      />
      <div className={styles.commonContainer}>
        <CommunicationGateway
          form={form}
          communicationInitial={communicationInitial}
          disableEdit={!add_edit_communication_gateway}
          onSaveSuccess={_initializeCommunicationGateway}
        />
        {communicationUuid !== NEW && (
          <Fragment>
            {gateway !== CommunicationGatewayProvider.Zhima && (
              <Webhooks
                text={getI18n('storeCommunicationSetting.detail.webhooksText')}
                url={communicationInitial.webhookUrl || ''}
                link='/'
                linkText={getI18n(
                  'storeCommunicationSetting.detail.webhooksLinkText',
                )}
              />
            )}
            {gateway !== CommunicationGatewayProvider.Zhima && (
              <Template
                groupedTemplates={groupedTemplates}
                update={_initializeCommunicationGateway}
                disableEdit={!add_edit_template}
                disableDelete={!delete_template}
                destination={
                  get(communicationInitial, [
                    'gateway',
                    'settings',
                    'destination',
                  ]) === DestinationType.Mainland
                }
              />
            )}
            <CommunicationChannel
              usedInSalesChannels={communicationInitial.usedInSalesChannels}
            />
          </Fragment>
        )}
      </div>
    </Spin>
  );
};

export default CommunicationDetail;
